import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { useTranslation } from "react-i18next";
import type { ChangeStatusDrawerInfo } from "../../hoc/withDrawers";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

const LANG_BASE = "vessel-call.port-services";
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const ACTUAL_NUMBER_OF_SKIPS_MAX_DIGITS = 6;

interface ActualNumberOfSkipsProps {
  serviceDetails: ChangeStatusDrawerInfo;
}

const ActualNumberOfSkips = ({
  serviceDetails: { numberOfSkips: currentNumberOfSkips },
}: ActualNumberOfSkipsProps) => {
  const { setActualNumberOfSkips, errors, setErrors } = usePortServicesFlow();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const parsedValue =
      currentNumberOfSkips === "" || currentNumberOfSkips === undefined
        ? undefined
        : typeof currentNumberOfSkips === "string"
          ? Number(currentNumberOfSkips)
          : currentNumberOfSkips;

    const validValue =
      parsedValue == null || Number.isNaN(parsedValue as number) ? undefined : parsedValue;

    setActualNumberOfSkips(validValue);
    setInputValue(validValue != null ? String(validValue) : "");
  }, [currentNumberOfSkips, setActualNumberOfSkips]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setInputValue("");
      setActualNumberOfSkips(undefined);

      if (errors.actualNumberOfSkips) {
        setErrors({
          ...errors,
          actualNumberOfSkips: undefined,
        });
      }
      return;
    }
    if (!POSITIVE_INTEGER_PATTERN.test(value)) return;

    if (value.length > ACTUAL_NUMBER_OF_SKIPS_MAX_DIGITS) {
      setErrors({
        ...errors,
        actualNumberOfSkips: t("form-validation.max-length-digits", {
          label: t(`${LANG_BASE}.fields.actual-number-of-skips`),
          maxDigits: ACTUAL_NUMBER_OF_SKIPS_MAX_DIGITS,
        }),
      });
      return;
    }

    const numberValue = Number(value);

    setInputValue(value);
    setActualNumberOfSkips(Number.isNaN(numberValue) ? undefined : numberValue);

    if (errors.actualNumberOfSkips) {
      setErrors({
        ...errors,
        actualNumberOfSkips: undefined,
      });
    }
  };

  const handleBlur = () => {
    if (!errors.actualNumberOfSkips) return;

    setErrors({
      ...errors,
      actualNumberOfSkips: undefined,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <CustomFormLabel data-testid="actualNumberOfSkips-label">
        {t(`${LANG_BASE}.fields.actual-number-of-skips`)}
      </CustomFormLabel>
      <Input
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        testId="actualNumberOfSkips-input"
        sx={{ width: "100%" }}
        placeholder={t(`${LANG_BASE}.fields.actual-number-of-skips`)}
        fullWidth
      />
      {errors.actualNumberOfSkips && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {errors.actualNumberOfSkips}
        </Typography>
      )}
    </div>
  );
};

export default ActualNumberOfSkips;
